import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Button, Input, InputNumber, Modal, Select, Table } from "antd"
import type { CheckboxProps } from "antd"
import { describeFinding, findingsByTable } from "../../model/reconcile"
import { resolveTable } from "../../model/resolve"
import { shownNames, variableSlice } from "../../model/shownLabels"
import { loopDrivers, sliceWords } from "../../model/summaries"
import {
  DisplayStatus,
  FINISHED,
  selectDisplayStatus,
  TableRun,
  useTabSpecStore,
  useTabSpecStoreApi,
} from "../../runtime/tabSpecStore"
import { Catalogue, findQuestion } from "../../types/catalogue"
import {
  Banner,
  BASE_RULES,
  BaseRule,
  newTabSpecId,
  ShownLabel,
  TABLE_KINDS,
  TableKind,
  TableSpec,
  TabSpecDefaults,
  TabSpecDocument,
} from "../../types/tabSpecDocument"
import {
  BASE_CHOICE_TEXT,
  describeKind,
  describeRows,
  KIND_TEXT,
  OVERRIDE_FIELDS,
  STATUS_TEXT,
} from "./describe"
import {
  GridRow,
  gridColumns,
  gridMinWidth,
  RowAction,
  rowName,
  TICK_WIDTH,
} from "./gridColumns"

/** Resolves tables before the questionnaire is in: every question reads as unknown. */
const EMPTY_CATALOGUE: Catalogue = {
  questions: [],
  knownPaths: null,
  hasRouting: false,
}

const TYPE_OPTIONS = TABLE_KINDS.map((k) => ({ value: k, label: KIND_TEXT[k] }))
const STATUS_OPTIONS = (Object.keys(STATUS_TEXT) as DisplayStatus[]).map(
  (s) => ({ value: s, label: STATUS_TEXT[s] })
)
/** The bulk bar's first choice: the override goes, the default applies again. */
const FOLLOW_DEFAULTS = "__defaults__"
const FOLLOW_OPTION = { value: FOLLOW_DEFAULTS, label: "Follow the defaults" }
const BASE_OPTIONS = [
  FOLLOW_OPTION,
  ...BASE_RULES.map((b) => ({ value: b, label: BASE_CHOICE_TEXT[b] })),
]
/** Without a questionnaire nobody is "asked": no routing says who was. */
const DATA_ONLY_BASE_OPTIONS = BASE_OPTIONS.filter((o) => o.value !== "asked")

/** Height of the grid's header row, taken off the measured area; tabSpec.css sets the header to it. */
const HEADER_HEIGHT = 40
const MIN_BODY_HEIGHT = 200

/**
 * The height of every row: two lines (the question and its text) and the
 * cell padding. No cell wraps (tabSpec.css), so every row the virtual list
 * measures has the height it estimated: the scroll height and the rows'
 * offsets stay put while it scrolls.
 */
const ROW_HEIGHT = 58
const ROW_STYLE: React.CSSProperties = { height: ROW_HEIGHT }
/**
 * The row height for the virtual list's estimate, so its scrollbar is right
 * before a row is measured. antd's props do not list it; antd hands it on
 * to rc-table's virtual table, which takes it.
 */
const VIRTUAL_ROWS = { listItemHeight: ROW_HEIGHT }

/** What a grid row was built from: it is built again only when one of these changes. */
interface RowSource {
  table: TableSpec
  defaults: TabSpecDefaults
  banners: Banner[]
  data: TabSpecDocument["data"]
  /** The book filter: every plan's population, so the status follows it. */
  filter: TabSpecDocument["filter"]
  catalogue: Catalogue | null
  /**
   * The shown labels of the table's own variable (`variableSlice`): the
   * names the row shows. A label of another variable leaves them as they are.
   */
  slice: string
  /** A grid summary's `driverSlices`: the words it names its loop iterations by. */
  drivers: string
  /** The shown labels: the plan, and so the status, reads those of the variables it names. */
  labels: ShownLabel[] | undefined
  generation: number
  run: TableRun | undefined
  row: GridRow
}

const sameTexts = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((text, i) => text === b[i])

/**
 * The shown labels of a grid summary's loop drivers (`variableSlice`): it
 * names the iterations it is fixed at by their shown answers, in its title
 * and its row. "" for any other table.
 */
const driverSlices = (
  table: TableSpec,
  catalogue: Catalogue,
  labels: ShownLabel[] | undefined
): string => {
  if (table.kind !== "grid-summary") return ""
  const question = findQuestion(catalogue, table.question, table.source)
  const drivers = question ? loopDrivers(question) : []
  return drivers.length > 0 ? JSON.stringify(drivers.map((d) => variableSlice(labels, d))) : ""
}

/** Clicks on a control inside a row do not select the row. */
const isControl = (target: EventTarget): boolean =>
  target instanceof Element &&
  target.closest(
    "input, button, label, a, .ant-checkbox-wrapper, .ant-select, .ant-table-selection-column"
  ) !== null

/**
 * The height the virtual grid gets: the measured height of its area, less
 * the header row. antd's virtual table needs a number.
 */
const useBodyHeight = (): [(node: HTMLDivElement | null) => void, number] => {
  const [height, setHeight] = useState(480)
  const observer = useRef<ResizeObserver | null>(null)
  const ref = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect()
    observer.current = null
    if (!node) return
    const update = (): void =>
      setHeight(Math.max(MIN_BODY_HEIGHT, node.clientHeight - HEADER_HEIGHT))
    update()
    if (typeof ResizeObserver === "undefined") return
    observer.current = new ResizeObserver(update)
    observer.current.observe(node)
  }, [])
  useEffect(() => () => observer.current?.disconnect(), [])
  return [ref, height]
}

/**
 * Every table of the spec, one row each in document order, with its settings
 * in short. Values that follow the defaults are muted. Clicking a row selects
 * the table; ticking rows opens the bulk bar; each row has a menu to move,
 * duplicate or remove it. In a project without a questionnaire the tables
 * are of variables, and "Who was asked" is not a base.
 */
const TableGrid: React.FC = () => {
  const store = useTabSpecStoreApi()
  const dataOnly = useTabSpecStore((s) => s.dataOnly)
  const document = useTabSpecStore((s) => s.document)
  const catalogue = useTabSpecStore((s) => s.catalogue)
  const findings = useTabSpecStore((s) => s.findings)
  const runs = useTabSpecStore((s) => s.runs)
  const generation = useTabSpecStore((s) => s.session.generation)
  const selectedId = useTabSpecStore((s) => s.selectedTableId)
  const aiTableIds = useTabSpecStore((s) => s.aiTableIds)
  const select = useTabSpecStore((s) => s.select)
  const setIncluded = useTabSpecStore((s) => s.setIncluded)
  const updateTable = useTabSpecStore((s) => s.updateTable)
  const clearOverrides = useTabSpecStore((s) => s.clearOverrides)
  const moveTable = useTabSpecStore((s) => s.moveTable)
  const addTables = useTabSpecStore((s) => s.addTables)
  const removeTable = useTabSpecStore((s) => s.removeTable)

  const [search, setSearch] = useState("")
  const [kind, setKind] = useState<TableKind | null>(null)
  const [status, setStatus] = useState<DisplayStatus | null>(null)
  const [ticked, setTicked] = useState<React.Key[]>([])
  const [moving, setMoving] = useState<GridRow | null>(null)
  const [position, setPosition] = useState<number | null>(null)
  const [bodyRef, bodyHeight] = useBodyHeight()
  const gridRef = useRef<HTMLDivElement>(null)
  // The dialog renders inside the spec, so it gets the --qc-* tokens.
  const dialogContainer = (): HTMLElement =>
    gridRef.current?.closest<HTMLElement>(".tabspec-root") ?? window.document.body

  // The rows as last built, by table id. A row keeps its object while
  // nothing it shows changes, so its cells do not render again: an edit of
  // one table builds one row, a run of one table builds its row alone.
  const built = useRef(new Map<string, RowSource>())
  const tableFindings = useMemo(() => findingsByTable(findings), [findings])
  const aiTables = useMemo(() => new Set(aiTableIds), [aiTableIds])
  const rows = useMemo((): GridRow[] => {
    const state = store.getState()
    const { defaults, banners, data, filter, tables, labels } = document
    const resolveWith = catalogue ?? EMPTY_CATALOGUE
    const names = shownNames(labels)
    const before = built.current
    const next = new Map<string, RowSource>()
    let number = 0
    const all = tables.map((table, index): GridRow => {
      const run = runs[table.id]
      const was = before.get(table.id)
      const slice = variableSlice(labels, table.question)
      const drivers = driverSlices(table, resolveWith, labels)
      // The resolved table follows the table, the defaults, the banners, the
      // catalogue and the shown labels of its variable (and of a grid
      // summary's loop drivers); the display status also follows the data
      // settings and the book filter (they are part of the plan), every
      // shown label (the plan reads those of the banner and the filter too),
      // the session and the run.
      const sameResolved =
        was !== undefined &&
        was.table === table &&
        was.defaults === defaults &&
        was.banners === banners &&
        was.catalogue === catalogue &&
        was.slice === slice &&
        was.drivers === drivers
      const resolved = sameResolved
        ? was.row.resolved
        : resolveTable(table, document, resolveWith, names)
      const sameStatus =
        sameResolved &&
        was.data === data &&
        was.filter === filter &&
        was.labels === labels &&
        was.generation === generation &&
        was.run === run
      const status = sameStatus ? was.row.status : selectDisplayStatus(state, table.id)
      // A run's reason and warnings count while the run is the one shown.
      const current = run && FINISHED.includes(run.status) && status === run.status
      const problems = [
        ...(tableFindings.get(table.id) ?? []).map((f) => describeFinding(f, names)),
        ...(current && run.message ? [run.message] : []),
        ...(current ? run.warnings : []),
      ]
      // What the row names, as the tables show it: made again with the resolved table.
      const instance = resolved.instance?.label ? resolved.instance : null
      const shownQuestion = sameResolved ? was.row.shownQuestion : names.variable(table.question)
      // A grid summary fixed at some values names them ("Online").
      const fixed =
        table.kind === "grid-summary" && table.slice && resolved.question
          ? sliceWords(resolved.question, table.slice, names) || null
          : null
      const shownInstance = sameResolved
        ? was.row.shownInstance
        : instance
          ? names.instance(table.question, instance.id, instance.label)
          : table.instance
            ? names.instance(table.question, table.instance, table.instance)
            : fixed
      const rowsText = sameResolved ? was.row.rowsText : describeRows(resolved, names)
      const kindText = sameResolved ? was.row.kindText : describeKind(resolved, names)
      const shownNumber = table.include ? ++number : null
      const last = index === tables.length - 1
      const ai = aiTables.has(table.id)
      const row: GridRow =
        was &&
        was.row.resolved === resolved &&
        was.row.status === status &&
        was.row.index === index &&
        was.row.number === shownNumber &&
        was.row.last === last &&
        was.row.shownQuestion === shownQuestion &&
        was.row.shownInstance === shownInstance &&
        was.row.rowsText === rowsText &&
        was.row.kindText === kindText &&
        was.row.ai === ai &&
        sameTexts(was.row.problems, problems)
          ? was.row
          : {
              id: table.id,
              index,
              number: shownNumber,
              last,
              table,
              resolved,
              status,
              problems,
              shownQuestion,
              shownInstance,
              rowsText,
              kindText,
              ai,
            }
      next.set(table.id, {
        table,
        defaults,
        banners,
        data,
        filter,
        catalogue,
        slice,
        drivers,
        labels,
        generation,
        run,
        row,
      })
      return row
    })
    built.current = next
    return all
  }, [store, document, catalogue, tableFindings, runs, generation, aiTables])

  // The label itself, the name the tables show and the question's text.
  const shownRows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return rows.filter(
      (r) =>
        (!needle ||
          r.table.question.toLowerCase().includes(needle) ||
          r.shownQuestion.toLowerCase().includes(needle) ||
          (r.resolved.question?.text ?? "").toLowerCase().includes(needle)) &&
        (kind === null || r.table.kind === kind) &&
        (status === null || r.status === status)
    )
  }, [rows, search, kind, status])

  // Ticks of tables that were removed since are dropped.
  const tickedIds = useMemo(
    () =>
      ticked
        .map(String)
        .filter((id) => document.tables.some((t) => t.id === id)),
    [ticked, document.tables]
  )

  const onAction = useCallback(
    (row: GridRow, action: RowAction): void => {
      switch (action) {
        case "up":
          moveTable(row.id, row.index - 1)
          return
        case "down":
          moveTable(row.id, row.index + 1)
          return
        case "position":
          setPosition(row.index + 1)
          setMoving(row)
          return
        case "duplicate": {
          const copy = { ...row.table, id: newTabSpecId("table"), origin: "added" as const }
          addTables([copy])
          moveTable(copy.id, row.index + 1)
          return
        }
        case "remove":
          if (store.getState().selectedTableId === row.id) select(null)
          removeTable(row.id)
          return
      }
    },
    [store, moveTable, addTables, removeTable, select]
  )

  const onInclude = useCallback(
    (id: string, include: boolean): void => setIncluded([id], include),
    [setIncluded]
  )
  const columns = useMemo(
    () =>
      gridColumns({
        dataOnly,
        onInclude,
        onSelect: select,
        onAction,
      }),
    [dataOnly, onInclude, select, onAction]
  )
  const searchWords = dataOnly ? "Search variables" : "Search questions"

  // Kept between renders, so the table and its rows get the same props.
  const scroll = useMemo(
    () => ({ x: gridMinWidth(columns), y: bodyHeight }),
    [columns, bodyHeight]
  )

  const rowSelection = useMemo(
    () => ({
      // Without a width antd's tick column would be the one to take what
      // the others leave; the Question column does that.
      columnWidth: TICK_WIDTH,
      selectedRowKeys: tickedIds,
      onChange: setTicked,
      // antd hands these to the input; its type does not list aria props.
      getCheckboxProps: (r: GridRow) => ({ "aria-label": `Tick ${rowName(r)}` }) as CheckboxProps,
    }),
    [tickedIds]
  )
  const rowClassName = useCallback(
    (r: GridRow): string => (r.id === selectedId ? "ts-selected" : ""),
    [selectedId]
  )
  const onRow = useCallback(
    (r: GridRow): React.HTMLAttributes<HTMLElement> => ({
      style: ROW_STYLE,
      // The table open in the inspector.
      "aria-selected": r.id === selectedId,
      onClick: (e) => {
        // Clicks in the row menu reach the row through the portal.
        if (!e.currentTarget.contains(e.target as Node)) return
        if (isControl(e.target)) return
        select(r.id)
      },
    }),
    [selectedId, select]
  )

  // A bulk action reaches only rows the user can see: a new search or filter
  // clears the ticks.
  const findWith = <T,>(set: (v: T) => void) => (v: T): void => {
    set(v)
    setTicked([])
  }

  const closeMove = (): void => {
    setMoving(null)
    setPosition(null)
  }
  const confirmMove = (): void => {
    if (moving && position !== null) moveTable(moving.id, position - 1)
    closeMove()
  }

  return (
    <div ref={gridRef} className="ts-grid">
      <div className="ts-toolbar" role="toolbar" aria-label="Find tables">
        <Input.Search
          allowClear
          size="small"
          className="ts-toolbar__search"
          placeholder={searchWords}
          aria-label={searchWords}
          value={search}
          onChange={(e) => findWith(setSearch)(e.target.value)}
        />
        <Select
          allowClear
          size="small"
          className="ts-toolbar__filter"
          placeholder="Type"
          aria-label="Type filter"
          value={kind}
          options={TYPE_OPTIONS}
          onChange={(v?: TableKind | null) => findWith(setKind)(v ?? null)}
        />
        <Select
          allowClear
          size="small"
          className="ts-toolbar__filter"
          placeholder="Status"
          aria-label="Status filter"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(v?: DisplayStatus | null) => findWith(setStatus)(v ?? null)}
        />
      </div>
      {tickedIds.length > 0 && (
        <div className="ts-bulk" role="toolbar" aria-label="Ticked tables">
          <span className="ts-bulk__count">{tickedIds.length} ticked</span>
          <Button size="small" onClick={() => setIncluded(tickedIds, true)}>
            Include
          </Button>
          <Button size="small" onClick={() => setIncluded(tickedIds, false)}>
            Exclude
          </Button>
          <Select
            size="small"
            className="ts-bulk__select"
            placeholder="Set banner"
            aria-label="Set banner"
            value={null}
            options={[
              FOLLOW_OPTION,
              ...document.banners.map((b) => ({ value: b.id, label: b.name })),
            ]}
            onChange={(bannerId: string) =>
              tickedIds.forEach((id) =>
                bannerId === FOLLOW_DEFAULTS
                  ? clearOverrides(id, ["bannerId"])
                  : updateTable(id, { bannerId })
              )
            }
          />
          <Select
            size="small"
            className="ts-bulk__select"
            placeholder="Set base"
            aria-label="Set base"
            value={null}
            options={dataOnly ? DATA_ONLY_BASE_OPTIONS : BASE_OPTIONS}
            onChange={(base: BaseRule | typeof FOLLOW_DEFAULTS) =>
              tickedIds.forEach((id) =>
                base === FOLLOW_DEFAULTS
                  ? clearOverrides(id, ["base"])
                  : updateTable(id, { base })
              )
            }
          />
          <Button
            size="small"
            onClick={() =>
              tickedIds.forEach((id) => clearOverrides(id, OVERRIDE_FIELDS))
            }
          >
            Reset to defaults
          </Button>
        </div>
      )}
      <div ref={bodyRef} className="ts-grid__body">
        <Table<GridRow>
          size="small"
          virtual
          {...VIRTUAL_ROWS}
          pagination={false}
          rowKey="id"
          columns={columns}
          dataSource={shownRows}
          scroll={scroll}
          rowSelection={rowSelection}
          rowClassName={rowClassName}
          onRow={onRow}
          locale={{ emptyText: "No table matches." }}
        />
      </div>
      <Modal
        open={moving !== null}
        title="Move to position"
        okText="Move"
        onOk={confirmMove}
        onCancel={closeMove}
        okButtonProps={{ disabled: position === null }}
        getContainer={dialogContainer}
        destroyOnHidden
      >
        <div className="ts-move">
          <InputNumber
            min={1}
            max={document.tables.length}
            precision={0}
            value={position}
            aria-label="Position"
            onChange={(v) => setPosition(typeof v === "number" ? v : null)}
            onPressEnter={confirmMove}
          />
          <span>of {document.tables.length}</span>
        </div>
      </Modal>
    </div>
  )
}

export default TableGrid
